import React, { useState } from 'react';

const CustomDateTimePicker = ({ onDateChange, initialStartDate, initialEndDate }) => {
	// Get yesterday's date
	const defaultDate = new Date();
	defaultDate.setDate(defaultDate.getDate() - 1);
	defaultDate.setHours(0, 0, 0, 0);

	// Generate time slots in 5 minute intervals
	const generateTimeSlots = () => {
		const slots = [];
		for (let hour = 0; hour < 24; hour++) {
			for (let minute = 0; minute < 60; minute += 5) {
				slots.push(
					`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
				);
			}
		}
		// Add 23:59 as the last slot
		slots.push('23:59');
		return slots;
	};

	const timeSlots = generateTimeSlots();

	// Format date for input default value (YYYY-MM-DD)
	const formatDateForInput = (date) => {
		// If date is string, convert to Date object
		const dateObj = typeof date === 'string' ? new Date(date) : date;

		// Check if valid date
		if (!(dateObj instanceof Date) || isNaN(dateObj)) {
			return formatDateForInput(defaultDate); // Return default date if invalid
		}

		const year = dateObj.getFullYear();
		const month = String(dateObj.getMonth() + 1).padStart(2, '0');
		const day = String(dateObj.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	};

	const [selectedDate, setSelectedDate] = useState(formatDateForInput(initialStartDate || defaultDate));
	const getTimeFromDate = (date) => {
		if (!date || !(date instanceof Date) || isNaN(date)) {
			return null;
		}
		return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
	};

	const [startTime, setStartTime] = useState(getTimeFromDate(initialStartDate) || '00:00');
	const [endTime, setEndTime] = useState(getTimeFromDate(initialEndDate) || '23:59');
	const [startDate, setStartDate] = useState(defaultDate);
	const [endDate, setEndDate] = useState(() => {
		const endDate = new Date(defaultDate);
		endDate.setHours(23, 59, 59, 59);
		return endDate;
	});

	const updateDates = (date, start, end) => {
		// Ensure date is a Date object
		const dateObj = typeof date === 'string' ? new Date(date) : date;

		const [startHour, startMinute] = start.split(':').map(Number);
		const [endHour, endMinute] = end.split(':').map(Number);

		const newStartDate = new Date(dateObj);
		newStartDate.setHours(startHour, startMinute, 0, 0);

		const newEndDate = new Date(dateObj);
		newEndDate.setHours(endHour, endMinute, 0, 0);

		setStartDate(newStartDate);
		setEndDate(newEndDate);

		if (onDateChange) {
			onDateChange({
				startDate: newStartDate,
				endDate: newEndDate,
			});
		}
	};

	const handleDateChange = (e) => {
		const newDate = e.target.value;
		setSelectedDate(newDate);
		updateDates(newDate, startTime, endTime);
	};

	const handleStartTimeChange = (e) => {
		const newStartTime = e.target.value;
		setStartTime(newStartTime);
		updateDates(selectedDate, newStartTime, endTime);
	};

	const handleEndTimeChange = (e) => {
		const newEndTime = e.target.value;
		setEndTime(newEndTime);
		updateDates(selectedDate, startTime, newEndTime);
	};

	return (
		<div className="mt-4 flex items-center space-x-4" style={{ display: 'flex' }}>
			<div className="flex items-center">
				<input
					type="date"
					value={selectedDate}
					onChange={handleDateChange}
					max={formatDateForInput(new Date())}
					className="border button dropdown floating icon labeled rounded selection ui"
					style={{
						height: '38px',
						backgroundColor: 'white',
					}}
				/>
			</div>

			<div className="flex items-center space-x-2">
				<select
					value={startTime}
					onChange={handleStartTimeChange}
					className="dropdown rounded ui"
					style={{
						height: '38px',
						backgroundColor: 'white',
						marginLeft: '10px',
					}}
				>
					{timeSlots.map((time) => (
						<option key={`start-${time}`} value={time}>
							{time}
						</option>
					))}
				</select>

				<span className="text-gray-500">-</span>

				<select
					value={endTime}
					onChange={handleEndTimeChange}
					className="dropdown rounded ui"
					style={{
						height: '38px',
						backgroundColor: 'white',
					}}
				>
					{timeSlots.map((time) => (
						<option key={`end-${time}`} value={time}>
							{time}
						</option>
					))}
				</select>
			</div>
		</div>
	);
};

export default CustomDateTimePicker;
